import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-agentic-platform',
  version: '1.0.0',
  properties: [
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select Access Agentic Platform credential',
      credentialTypes: [{ name: 'access-agentic-platform', required: true }],
    },
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'invokeAgent',
      options: [
        // Agent Invocation
        { value: 'invokeAgent', name: 'Invoke Agent' },
        { value: 'invokeAgentWithAttachments', name: 'Invoke Agent with Attachments' },
        { value: 'invokeAgentStream', name: 'Invoke Agent (Stream)' },
        { value: 'invokeAgentStreamWithAttachments', name: 'Invoke Agent Stream with Attachments' },
        { value: 'invokeAgentStreamAgUi', name: 'Invoke Agent Stream (AG-UI)' },
        // Team Analytics (no agentId required)
        { value: 'getAnalyticsSummary', name: 'Get Analytics Summary' },
        { value: 'getAnalyticsAgents', name: 'Get Agents Analytics' },
        { value: 'getAnalyticsWorkflows', name: 'Get Workflows Analytics' },
        { value: 'getAnalyticsTools', name: 'Get Tools Analytics' },
        { value: 'getAnalyticsErrors', name: 'Get Errors Analytics' },
        // Agent-specific Analytics (agentId required)
        { value: 'getAgentToolsAnalytics', name: 'Get Agent Tools Analytics' },
        { value: 'getAgentErrorsAnalytics', name: 'Get Agent Errors Analytics' },
      ],
    },

    // === Agent Selection with modeToggle (for invocation operations) ===
    {
      key: 'agentId',
      label: 'Agent',
      type: 'string',
      required: true,
      placeholder: 'Select an agent or enter ID',
      description: 'The agent to invoke',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getAgents',
          dependsOn: [],
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
      displayOptions: {
        show: {
          operation: [
            'invokeAgent',
            'invokeAgentWithAttachments',
            'invokeAgentStream',
            'invokeAgentStreamWithAttachments',
            'invokeAgentStreamAgUi',
          ],
        },
      },
    },

    // === Agent Selection for Analytics (separate field for clarity) ===
    {
      key: 'analyticsAgentId',
      label: 'Agent',
      type: 'string',
      required: true,
      placeholder: 'Select an agent or enter ID',
      description: 'The agent to get analytics for',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getAgents',
          dependsOn: [],
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
      displayOptions: {
        show: {
          operation: [
            'getAgentToolsAnalytics',
            'getAgentErrorsAnalytics',
          ],
        },
      },
    },

    // === Message for Agent Invocation ===
    {
      key: 'message',
      label: 'Message',
      type: 'text',
      required: true,
      placeholder: 'Enter your message or prompt for the agent',
      description: 'The message/prompt to send to the agent',
      typeOptions: {
        rows: 4,
      },
      displayOptions: {
        show: {
          operation: [
            'invokeAgent',
            'invokeAgentWithAttachments',
            'invokeAgentStream',
            'invokeAgentStreamWithAttachments',
            'invokeAgentStreamAgUi',
          ],
        },
      },
    },

    // === Conversation ID (optional) ===
    {
      key: 'conversationId',
      label: 'Conversation ID',
      type: 'string',
      required: false,
      placeholder: 'Optional: conversation-uuid',
      description: 'Optional conversation ID to maintain context across multiple invocations',
      displayOptions: {
        show: {
          operation: [
            'invokeAgent',
            'invokeAgentWithAttachments',
            'invokeAgentStream',
            'invokeAgentStreamWithAttachments',
            'invokeAgentStreamAgUi',
          ],
        },
      },
    },

    // === Division (other EvoRegistration fields are auto-filled from workflow owner context) ===
    {
      key: 'division',
      label: 'Division',
      type: 'options',
      required: true,
      default: 'Evo',
      description: 'The division for the registration',
      options: [
        { value: 'Evo', name: 'Evo' },
        { value: 'Ppl', name: 'People' },
        { value: 'Hos', name: 'Hospitality' },
        { value: 'Erp', name: 'ERP' },
        { value: 'Hsc', name: 'HSC' },
        { value: 'Npe', name: 'Non Profit & Education' },
        { value: 'Rec', name: 'Recruitment' },
        { value: 'Apac', name: 'APAC' },
        { value: 'Lgl', name: 'Legal' },
        { value: 'Pay', name: 'Payments' },
      ],
      displayOptions: {
        show: {
          operation: [
            'invokeAgent',
            'invokeAgentWithAttachments',
            'invokeAgentStream',
            'invokeAgentStreamWithAttachments',
            'invokeAgentStreamAgUi',
          ],
        },
      },
    },
    // === Additional Context (for standard invoke) ===
    {
      key: 'additionalContext',
      label: 'Additional Context',
      type: 'json',
      required: false,
      placeholder: '{"key": "value"}',
      description: 'Optional additional context data as JSON object',
      displayOptions: {
        show: {
          operation: ['invokeAgent'],
        },
      },
    },

    // === Attachments (for attachment operations) ===
    {
      key: 'attachments',
      label: 'Attachments',
      type: 'json',
      required: false,
      placeholder: '[{"fileName": "doc.pdf", "content": "base64..."}]',
      description: 'JSON array of attachments to include with the invocation',
      displayOptions: {
        show: {
          operation: ['invokeAgentWithAttachments', 'invokeAgentStreamWithAttachments'],
        },
      },
    },

    // === Date filters for Analytics ===
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'dateTime',
      required: false,
      placeholder: '2024-01-01T00:00:00Z',
      description: 'Start date for analytics filter (ISO 8601 format)',
      displayOptions: {
        show: {
          operation: [
            'getAnalyticsSummary',
            'getAnalyticsAgents',
            'getAnalyticsWorkflows',
            'getAnalyticsTools',
            'getAnalyticsErrors',
            'getAgentToolsAnalytics',
            'getAgentErrorsAnalytics',
          ],
        },
      },
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'dateTime',
      required: false,
      placeholder: '2024-12-31T23:59:59Z',
      description: 'End date for analytics filter (ISO 8601 format)',
      displayOptions: {
        show: {
          operation: [
            'getAnalyticsSummary',
            'getAnalyticsAgents',
            'getAnalyticsWorkflows',
            'getAnalyticsTools',
            'getAnalyticsErrors',
            'getAgentToolsAnalytics',
            'getAgentErrorsAnalytics',
          ],
        },
      },
    },
  ],
};

export default schema;
